import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Box, Tabs, ButtonGroup, Button } from '@ally/metronome-ui'
import { useAccountTypes } from '../AccountTypeContext'
import { useCustomizeDashboard } from './CustomizeDashboardContext/CustomizeDashboardContext'
import ManageWidgets from './ManageWidgets'
import CustomizeApiAlert from './CustomizeApiAlert'
import { DataTypeName, DataType, AccountTypesObj, OrderObj } from './types'
import OrganizeAccounts from './OrganizeAccounts'
import { trackCustomizationModal } from '../analytics'

const StyledBox = styled(Box)`
  ${({ theme: { colors } }): string => `
    [data-reach-tab-list] {
      button {
        position: unset;
        height: auto;
        padding: 0 0 2px 0;
        border: none;
        background-color: ${colors.white};
        color: ${colors.bluesuedeshoes};
        font-weight: 700;

        &:first-of-type {
          margin-right: 30px;
        }

        &[data-selected] {
          color: ${colors.plum};
          border-bottom: 2px solid ${colors.plum};
        }
      }
    }

    [data-reach-tab-panel] {
      border: none;
      box-shadow: none;
      padding: 15px 0 0 0;
    }
  `};
`

const StyledHeading = styled(Box)`
  ${({ theme: { colors, fontSizes, space } }): string => `
    display: inline-block;
    color: ${colors.plum};
    font-size: ${fontSizes.md};
    font-weight: bold;
    margin-bottom: ${space.sm};
    padding-bottom: 6px;
    border-bottom: 2px solid ${colors.plum};
  `};
`
interface CustomizeTabsProps {
  headingRef: React.RefObject<HTMLHeadingElement>
  defaultTab: number
}

const CustomizeTabs: React.FC<CustomizeTabsProps> = ({
  headingRef,
  defaultTab,
}) => {
  const { hasSingleAccountType, isDebtPlanUser } = useAccountTypes()
  const [
    {
      isCustomizationEnabled,
      eligibleWidgets,
      hasCustomizeChangesToSave,
      customizeApiAlert,
      customizeApiData,
      saveCustomizations,
      isSaving,
      customizeDataToPatch,
      widgets,
      isDefaultWidgetsOnActive,
    },
    dispatch,
  ] = useCustomizeDashboard()

  const canReorderAccounts = isCustomizationEnabled && !hasSingleAccountType
  const canManageWidgets =
    isCustomizationEnabled && Object.keys(eligibleWidgets).length > 0
  const showTabs =
    isCustomizationEnabled &&
    (!hasSingleAccountType || isDebtPlanUser) &&
    canManageWidgets
  const itemsRef: HTMLElement[] = []

  useEffect(() => {
    if (showTabs) {
      trackCustomizationModal({ activeTab: 'Organize' })
    } else {
      const activeTab = canReorderAccounts ? 'Organize' : 'Manage'
      trackCustomizationModal({ activeTab })
    }
  }, [canReorderAccounts, showTabs])

  const handleTabChange = (i: number): void => {
    itemsRef[i].focus()
    dispatch({ type: 'RESET CustomizeDashboard API alert' })
    const activeTab = i === 0 ? 'Organize' : 'Manage'
    trackCustomizationModal({ activeTab })
  }

  const handleRef = (el: HTMLElement | null): void => {
    if (el) {
      itemsRef.push(el)
    }
  }

  function onListChange(
    type: DataTypeName,
    list: DataType<DataTypeName>[],
  ): void {
    const getAccountTypeData = (): AccountTypesObj[] => {
      const accountTypes =
        customizeDataToPatch.accountTypes ?? customizeApiData.accountTypes
      const accountTypeList = list as AccountTypesObj[]
      // the index of the first account type with the matching LOB for the list that the user reordered
      const index = accountTypes.findIndex(
        accType => accType.lob === accountTypeList[0].lob,
      )
      const lengthOfRelatedAccountTypes = accountTypeList.filter(
        accType => accType.lob === accountTypeList[0].lob,
      ).length
      // create copy of accountTypes because splice below modifies the array
      const newAccountTypes = accountTypes.slice()

      // use splice to remove existing account types and insert the new ones in the same place
      // while retaining the original order of the other account types
      newAccountTypes.splice(
        index, // starting place
        lengthOfRelatedAccountTypes, // number of items to remove
        ...accountTypeList, // items added to the list
      )

      return newAccountTypes
    }

    const data = ((): (AccountTypesObj | OrderObj)[] => {
      switch (type) {
        case 'accountTypes':
          return getAccountTypeData()
        case 'widgets':
          if (!isDefaultWidgetsOnActive) return list
          return list.concat(
            (customizeDataToPatch.widgets ?? widgets).filter(
              w => w.enabled === false,
            ),
          )
        default:
          return list
      }
    })()

    dispatch({
      type: 'Add items to customizeDataToPatch',
      payload: {
        [type]: data,
      },
    })
  }

  const getTabName = (): string => {
    if (canReorderAccounts) {
      if (isDebtPlanUser) {
        return 'Organize Accounts and Plans'
      }
      return 'Organize Accounts'
    }
    if (isDebtPlanUser) {
      return 'Organize Plans'
    }
    return ''
  }

  const tabName = getTabName()

  return (
    <StyledBox>
      <Box mt="md" mb={!showTabs ? 'md' : null}>
        <CustomizeApiAlert
          shouldRender={
            !customizeApiAlert.isDashboardError &&
            customizeApiAlert.shouldRender
          }
          type={customizeApiAlert.type}
          isDashboardError={customizeApiAlert.isDashboardError}
        />
      </Box>
      {showTabs && (
        <Tabs.Wrapper
          onChange={handleTabChange}
          suppressMdDownLayout
          mt="md"
          defaultIndex={defaultTab}
        >
          <Tabs.TabWithPanel
            tab={tabName}
            allytmln={`${tabName} Tab`}
            panel={
              <OrganizeAccounts
                handleRef={handleRef}
                onListChange={onListChange}
              />
            }
          />
          <Tabs.TabWithPanel
            tab="Manage Widgets"
            allytmln="Manage Widgets Tab"
            panel={
              <ManageWidgets
                handleRef={handleRef}
                headingRef={headingRef}
                onListChange={onListChange}
              />
            }
          />
        </Tabs.Wrapper>
      )}
      {canReorderAccounts && !showTabs && (
        <>
          <StyledHeading>Organize Accounts</StyledHeading>
          <OrganizeAccounts handleRef={handleRef} onListChange={onListChange} />
        </>
      )}
      {canManageWidgets && !showTabs && (
        <>
          <StyledHeading>Manage Widgets</StyledHeading>
          <ManageWidgets
            handleRef={handleRef}
            headingRef={headingRef}
            onListChange={onListChange}
          />
        </>
      )}

      {hasCustomizeChangesToSave && (
        <Box mt="md">
          <ButtonGroup>
            <Button
              variant="primary"
              text="Save"
              allytmln="Save Customize Accounts Model"
              isLoading={isSaving}
              onClick={(): void => saveCustomizations()}
            />
            <Button
              variant="link"
              text="Cancel"
              allytmln="Cancel Customize Accounts Model"
              onClick={(): void => {
                dispatch({ type: 'set isCustomizeModalOpen', payload: false })
              }}
              disabled={isSaving}
              aria-disabled={isSaving}
            />
          </ButtonGroup>
        </Box>
      )}
    </StyledBox>
  )
}

export default CustomizeTabs
